import React, { useState, useRef, useCallback, useEffect } from 'react';
import ImageEditor from './ImageEditor';
import './ScreenCapture.css';

const ScreenCapture = ({ onCapture, onClose, language = 'en', maxImages = 4 }) => {
    const [capturedImages, setCapturedImages] = useState([]);
    const [editingImage, setEditingImage] = useState(null);
    const [editingIndex, setEditingIndex] = useState(null);
    const [isCapturing, setIsCapturing] = useState(false);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('screen'); // 'screen', 'window', 'tab'
    const pasteInputRef = useRef(null);

    const t = {
        ar: {
            title: 'التقاط صورة للشاشة',
            subtitle: 'اختر ما تريد مشاركته',
            chromeTab: 'تبويب Chrome',
            window: 'نافذة',
            entireScreen: 'الشاشة بالكامل',
            selectTab: 'اختر تبويب للمشاركة',
            selectWindow: 'اختر نافذة للمشاركة',
            selectScreen: 'اختر شاشة للمشاركة',
            capture: 'التقاط',
            cancel: 'إلغاء',
            done: 'تم',
            pasteHint: 'أو اضغط Ctrl+V للصق صورة',
            dragHint: 'أو اسحب وأفلت صورة هنا',
            maxImagesError: `الحد الأقصى ${maxImages} صور`,
            captureError: 'فشل التقاط الشاشة',
            browserNotSupported: 'المتصفح لا يدعم التقاط الشاشة',
            edit: 'تعديل',
            delete: 'حذف',
            images: 'صور',
            permissionDenied: 'تم رفض الإذن لالتقاط الشاشة'
        },
        en: {
            title: 'Screen Capture',
            subtitle: 'Choose what to share',
            chromeTab: 'Chrome Tab',
            window: 'Window',
            entireScreen: 'Entire Screen',
            selectTab: 'Select a tab to share',
            selectWindow: 'Select a window to share',
            selectScreen: 'Select a screen to share',
            capture: 'Capture',
            cancel: 'Cancel',
            done: 'Done',
            pasteHint: 'Or press Ctrl+V to paste image',
            dragHint: 'Or drag and drop an image here',
            maxImagesError: `Maximum ${maxImages} images allowed`,
            captureError: 'Failed to capture screen',
            browserNotSupported: 'Browser does not support screen capture',
            edit: 'Edit',
            delete: 'Delete',
            images: 'images',
            permissionDenied: 'Permission denied for screen capture'
        }
    }[language] || {};

    // Handle paste event
    useEffect(() => {
        const handlePaste = (e) => {
            const items = e.clipboardData?.items;
            if (!items) return;

            for (let item of items) {
                if (item.type.startsWith('image/')) {
                    e.preventDefault();
                    const file = item.getAsFile();
                    if (file) {
                        processImageFile(file);
                    }
                    break;
                }
            }
        };

        document.addEventListener('paste', handlePaste);
        return () => document.removeEventListener('paste', handlePaste);
    }, [capturedImages.length]);

    // Compress image to target size - IMPROVED QUALITY
    const compressImage = async (dataUrl, maxSizeKB = 4000) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let { width, height } = img;
                
                // Max dimensions - INCREASED for better quality
                const maxDim = 2560;
                if (width > maxDim || height > maxDim) {
                    if (width > height) {
                        height = (height / width) * maxDim;
                        width = maxDim;
                    } else {
                        width = (width / height) * maxDim;
                        height = maxDim;
                    }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                // Try PNG first for screenshots (better quality)
                let result = canvas.toDataURL('image/png');
                
                // Only compress to JPEG if PNG is too large
                if (result.length > maxSizeKB * 1024 * 1.37) {
                    let quality = 0.95;
                    while (quality > 0.5) {
                        result = canvas.toDataURL('image/jpeg', quality);
                        if (result.length <= maxSizeKB * 1024 * 1.37) break;
                        quality -= 0.05;
                    }
                }

                resolve(result);
            };
            img.src = dataUrl;
        });
    };

    // Process image file (from paste or drag)
    const processImageFile = async (file) => {
        if (capturedImages.length >= maxImages) {
            setError(t.maxImagesError);
            return;
        }

        const reader = new FileReader();
        reader.onload = async (e) => {
            const compressed = await compressImage(e.target.result);
            setCapturedImages(prev => [...prev, compressed]);
            setError('');
        };
        reader.readAsDataURL(file);
    };

    // Handle drag and drop
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const files = e.dataTransfer?.files;
        if (files?.length > 0) {
            const file = files[0];
            if (file.type.startsWith('image/')) {
                processImageFile(file);
            }
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    // Screen capture using getDisplayMedia
    const captureScreen = async (displaySurface) => {
        if (!navigator.mediaDevices?.getDisplayMedia) {
            setError(t.browserNotSupported);
            return;
        }

        if (capturedImages.length >= maxImages) {
            setError(t.maxImagesError);
            return;
        }

        setIsCapturing(true);
        setError('');

        try {
            const constraints = {
                video: {
                    displaySurface: displaySurface, // 'monitor', 'window', 'browser'
                    width: { ideal: 1920 },
                    height: { ideal: 1080 }
                },
                audio: false
            };

            // For Chrome, we can suggest the display surface
            if (displaySurface === 'tab') {
                constraints.video.displaySurface = 'browser';
                constraints.preferCurrentTab = false;
            } else if (displaySurface === 'window') {
                constraints.video.displaySurface = 'window';
            } else {
                constraints.video.displaySurface = 'monitor';
            }

            const stream = await navigator.mediaDevices.getDisplayMedia(constraints);
            const track = stream.getVideoTracks()[0];
            
            // Create ImageCapture if supported, otherwise use video element
            if (typeof ImageCapture !== 'undefined') {
                const imageCapture = new ImageCapture(track);
                const bitmap = await imageCapture.grabFrame();
                
                const canvas = document.createElement('canvas');
                canvas.width = bitmap.width;
                canvas.height = bitmap.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(bitmap, 0, 0);
                
                const dataUrl = canvas.toDataURL('image/png');
                const compressed = await compressImage(dataUrl);
                setCapturedImages(prev => [...prev, compressed]);
            } else {
                // Fallback: use video element
                const video = document.createElement('video');
                video.srcObject = stream;
                video.play();
                
                await new Promise(resolve => {
                    video.onloadedmetadata = () => {
                        setTimeout(resolve, 100);
                    };
                });

                const canvas = document.createElement('canvas');
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(video, 0, 0);
                
                const dataUrl = canvas.toDataURL('image/png');
                const compressed = await compressImage(dataUrl);
                setCapturedImages(prev => [...prev, compressed]);
            }

            // Stop all tracks
            stream.getTracks().forEach(track => track.stop());
        } catch (err) {
            console.error('Screen capture error:', err);
            if (err.name === 'NotAllowedError') {
                setError(t.permissionDenied);
            } else {
                setError(t.captureError);
            }
        } finally {
            setIsCapturing(false);
        }
    };

    // Delete image
    const handleDeleteImage = (index) => {
        setCapturedImages(prev => prev.filter((_, i) => i !== index));
    };

    // Edit image
    const handleEditImage = (index) => {
        setEditingImage(capturedImages[index]);
        setEditingIndex(index);
    };

    // Save edited image
    const handleSaveEdit = async (editedDataUrl) => {
        const compressed = await compressImage(editedDataUrl);
        setCapturedImages(prev => {
            const newImages = [...prev];
            newImages[editingIndex] = compressed;
            return newImages;
        });
        setEditingImage(null);
        setEditingIndex(null);
    };

    // Cancel edit
    const handleCancelEdit = () => {
        setEditingImage(null);
        setEditingIndex(null);
    };

    // Done - send images back
    const handleDone = () => {
        // Convert data URLs to File objects
        const files = capturedImages.map((dataUrl, index) => {
            const arr = dataUrl.split(',');
            const mime = arr[0].match(/:(.*?);/)[1];
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }
            const ext = mime.includes('webp') ? 'webp' : 'jpg';
            return new File([u8arr], `screenshot-${Date.now()}-${index}.${ext}`, { type: mime });
        });
        
        onCapture(files);
        onClose();
    };

    // If editing, show image editor
    if (editingImage) {
        return (
            <ImageEditor
                image={editingImage}
                onSave={handleSaveEdit}
                onCancel={handleCancelEdit}
                language={language}
            />
        );
    }

    return (
        <div className="screen-capture-overlay" onClick={onClose}>
            <div 
                className="screen-capture-modal" 
                onClick={e => e.stopPropagation()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
            >
                <div className="sc-header">
                    <h2>{t.title}</h2>
                    <p>{t.subtitle}</p>
                </div>

                {error && <div className="sc-error">{error}</div>}

                <div className="sc-tabs">
                    <button 
                        className={`sc-tab ${activeTab === 'tab' ? 'active' : ''}`}
                        onClick={() => setActiveTab('tab')}
                    >
                        {t.chromeTab}
                    </button>
                    <button 
                        className={`sc-tab ${activeTab === 'window' ? 'active' : ''}`}
                        onClick={() => setActiveTab('window')}
                    >
                        {t.window}
                    </button>
                    <button 
                        className={`sc-tab ${activeTab === 'screen' ? 'active' : ''}`}
                        onClick={() => setActiveTab('screen')}
                    >
                        {t.entireScreen}
                    </button>
                </div>

                <div className="sc-content">
                    <div className="sc-capture-area">
                        <div className="sc-capture-placeholder">
                            {activeTab === 'tab' && <p>{t.selectTab}</p>}
                            {activeTab === 'window' && <p>{t.selectWindow}</p>}
                            {activeTab === 'screen' && <p>{t.selectScreen}</p>}
                            
                            <button 
                                className="sc-capture-btn"
                                onClick={() => captureScreen(activeTab)}
                                disabled={isCapturing || capturedImages.length >= maxImages}
                            >
                                {isCapturing ? '⏳' : '📷'} {t.capture}
                            </button>

                            <p className="sc-hint">{t.pasteHint}</p>
                            <p className="sc-hint">{t.dragHint}</p>
                        </div>
                    </div>

                    {/* Captured Images Preview */}
                    {capturedImages.length > 0 && (
                        <div className="sc-preview-section">
                            <h4>{capturedImages.length} {t.images}</h4>
                            <div className="sc-preview-grid">
                                {capturedImages.map((img, index) => (
                                    <div key={index} className="sc-preview-item">
                                        <img src={img} alt={`Screenshot ${index + 1}`} />
                                        <div className="sc-preview-actions">
                                            <button onClick={() => handleEditImage(index)} title={t.edit}>
                                                ✏️
                                            </button>
                                            <button onClick={() => handleDeleteImage(index)} title={t.delete}>
                                                🗑️
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="sc-footer">
                    <button className="sc-btn sc-btn-cancel" onClick={onClose}>
                        {t.cancel}
                    </button>
                    <button 
                        className="sc-btn sc-btn-done" 
                        onClick={handleDone}
                        disabled={capturedImages.length === 0}
                    >
                        {t.done} ({capturedImages.length})
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ScreenCapture;
